// src/DataContext.js
import React, { createContext, useState, useEffect } from 'react';

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
    const [state, setState] = useState({
        traitsData: null, // Start with null to differentiate between not loaded and empty data
        auctionData: null,
        traitIdToName: null,
        extracts: null,
        loading: true,
        error: null,
    });

    const PROXY_URL = process.env.REACT_APP_PROXY_URL || 'proxy.php';

    useEffect(() => {
        let isMounted = true; // To avoid setting state on unmounted component
        let intervalId = null; // Will hold the interval ID

        async function fetchData() {
            console.time('Total Data Fetch and Process Time');
            console.log('Fetching data from:', PROXY_URL);

            try {
                // Start fetching
                console.time('Fetching Data');
                const response = await fetch(PROXY_URL);
                console.timeEnd('Fetching Data');

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                console.time('Parsing JSON');
                const jsonData = await response.json();
                console.timeEnd('Parsing JSON');

                if (jsonData.error) {
                    throw new Error(jsonData.error);
                }

                const data = jsonData.result.data;
                console.log('Data fetched successfully:', data.length, 'items');

                // Start processing data
                console.time('Processing Data');

                // Initialize traitIdToNameMap
                console.time('Building traitIdToNameMap');
                const traitIdToNameMap = {};
                data.forEach(item => {
                    if (item.traitIds) {
                        Object.entries(item.traitIds).forEach(([key, value]) => {
                            traitIdToNameMap[key] = value;
                        });
                    }
                });
                console.timeEnd('Building traitIdToNameMap');

                // Initialize auctionDataList and extractsMap
                console.time('Building auctionDataList and extractsMap');
                const extractsMap = {};
                const auctionDataList = [];

                data.forEach(item => {
                    if (item.mainCategory === "traitextract") {
                        item.traitItems.forEach(traitItem => {
                            const traitName = traitIdToNameMap[traitItem.traitId];
                            const newItem = {
                                id: item.id,
                                name: `${item.name} - ${traitName}`,
                                grade: item.grade,
                                inStock: traitItem.inStock,
                                minPrice: traitItem.minPrice,
                                mainCategory: item.mainCategory,
                                subSubCategory: item.subSubCategory,
                            };
                            auctionDataList.push(newItem);

                            // Build extracts
                            const grade = item.grade;
                            const subCategory = item.subCategory;
                            const subSubCategory = item.subSubCategory;

                            if (!extractsMap[grade]) extractsMap[grade] = {};
                            if (!extractsMap[grade][subCategory]) extractsMap[grade][subCategory] = {};
                            if (!extractsMap[grade][subCategory][subSubCategory]) extractsMap[grade][subCategory][subSubCategory] = {};

                            if (!extractsMap[grade][subCategory][subSubCategory][traitName]) {
                                extractsMap[grade][subCategory][subSubCategory][traitName] = [];
                            }

                            extractsMap[grade][subCategory][subSubCategory][traitName].push({
                                id: item.id,
                                name: item.name,
                                inStock: traitItem.inStock,
                                minPrice: traitItem.minPrice,
                            });
                        });
                    } else {
                        const newItem = {
                            id: item.id,
                            name: item.name,
                            grade: item.grade,
                            inStock: item.inStock,
                            minPrice: item.minPrice,
                            mainCategory: item.mainCategory,
                            traitIds: item.traitIds,
                            traitItems: item.traitItems,
                            subCategory: item.subCategory,
                            subSubCategory: item.subSubCategory,
                        };
                        auctionDataList.push(newItem);
                    }
                });
                console.timeEnd('Building auctionDataList and extractsMap');

                // End processing data
                console.timeEnd('Processing Data');

                // Now set the state all at once
                if (isMounted) {
                    console.time('Setting State');
                    setState({
                        traitsData: data,
                        auctionData: auctionDataList,
                        traitIdToName: traitIdToNameMap,
                        extracts: extractsMap,
                        loading: false,
                        error: null,
                    });
                    console.timeEnd('Setting State');
                }

            } catch (error) {
                console.error('Error fetching data:', error);
                if (isMounted) {
                    setState(prevState => ({
                        ...prevState,
                        loading: false,
                        error: error.message,
                    }));
                }
            } finally {
                console.timeEnd('Total Data Fetch and Process Time');
            }
        }

        // Fetch data immediately on mount
        fetchData();

        // Set up interval to fetch data every 5 minutes (300,000 milliseconds)
        intervalId = setInterval(() => {
            if (isMounted) {
                fetchData();
            }
        }, 5 * 60 * 1000);

        // Cleanup function to avoid setting state on unmounted component
        return () => {
            isMounted = false;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [PROXY_URL]);

    return (
        <DataContext.Provider value={state}>
            {children}
        </DataContext.Provider>
    );
};
